import { useState } from 'react'
import { Banner, Button, Card, Input, Lightbox } from '../../design/components'
import { reuseBlocks } from './ComposerTools'
import type { DraftActions } from './queries'
import { draftUrl, type ComposerBlock, type ComposerDraft } from './types'
import styles from './composer.module.css'

export interface DraftResultsProps {
  drafts: readonly ComposerDraft[]
  actions: DraftActions
  /** Adds a result to the prompt as a reference — the "keep this, change that" move. */
  onReuse: (blocks: ComposerBlock[]) => void
  /** What a downloaded file is called. "portrait" or "battle-map" — never the generic word. */
  downloadName?: string
}

/**
 * What the model made, before anyone has decided to keep any of it.
 *
 * **Nothing here is in the gallery.** That is the composer's shape and the card says so out
 * loud, because a picture that looks saved and is not is the one misunderstanding that costs
 * real work. Four things can happen to a result: it becomes a gallery image, it becomes a
 * reference for the next attempt, it gets downloaded, or it goes away.
 */
export function DraftResults({
  drafts,
  actions,
  onReuse,
  downloadName = 'result',
}: DraftResultsProps) {
  const [openAt, setOpenAt] = useState<number | null>(null)

  if (drafts.length === 0) return null

  const full = drafts.map((draft, index) => ({
    src: draftUrl(draft),
    alt: `Result ${index + 1}`,
    caption: new Date(draft.createdAt).toLocaleTimeString(),
  }))

  return (
    <Card
      variant="bio"
      label="Results"
      hint={drafts.length === 1 ? '1 unsaved' : `${drafts.length} unsaved`}
    >
      <p className={styles.note}>
        None of these is in the gallery yet. Save the ones worth keeping — the rest are swept after
        a couple of hours.
      </p>

      {actions.failure !== null && (
        <Banner
          tone="error"
          actions={
            <Button variant="secondary" onClick={actions.dismissFailure}>
              Dismiss
            </Button>
          }
        >
          {actions.failure}
        </Banner>
      )}

      {/* Not an error — a question. A location holds one battle map, so saving a second is a
          replacement, and replacing something is a decision rather than a side effect. */}
      {actions.conflict !== null && (
        <Banner
          tone="warn"
          actions={
            <>
              <Button variant="secondary" onClick={actions.dismissConflict}>
                Keep the old one
              </Button>
              <Button
                variant="danger"
                disabled={actions.save.isPending}
                onClick={() =>
                  actions.save.mutate({ draftId: actions.conflict!, replaceExisting: true })
                }
              >
                Replace the map
              </Button>
            </>
          }
        >
          This place already has a battle map. Saving this one replaces it — the old map is gone for
          good.
        </Banner>
      )}

      <div className={styles.draftGrid}>
        {drafts.map((draft, index) => (
          <DraftTile
            key={draft.draftId}
            draft={draft}
            index={index}
            actions={actions}
            downloadName={downloadName}
            onOpen={() => setOpenAt(index)}
            onReuse={onReuse}
          />
        ))}
      </div>

      {openAt !== null && <Lightbox images={full} index={openAt} onClose={() => setOpenAt(null)} />}
    </Card>
  )
}

function DraftTile({
  draft,
  index,
  actions,
  downloadName,
  onOpen,
  onReuse,
}: {
  draft: ComposerDraft
  index: number
  actions: DraftActions
  downloadName: string
  onOpen: () => void
  onReuse: (blocks: ComposerBlock[]) => void
}) {
  const [saving, setSaving] = useState(false)
  const [title, setTitle] = useState('')

  return (
    <div className={styles.draft}>
      <button
        type="button"
        className={styles.draftImageButton}
        onClick={onOpen}
        aria-label={`View result ${index + 1} full screen`}
      >
        <img className={styles.draftImage} src={draftUrl(draft)} alt="" />
      </button>

      <span className={styles.rowMeta}>
        {draft.imageType === undefined
          ? new Date(draft.createdAt).toLocaleTimeString()
          : `${draft.imageType === 'BattleMap' ? 'Battle map' : 'Depiction'} · ${new Date(draft.createdAt).toLocaleTimeString()}`}
      </span>

      {saving ? (
        <form
          className={styles.inlineForm}
          onSubmit={(event) => {
            event.preventDefault()
            actions.save.mutate(
              { draftId: draft.draftId, title },
              { onSuccess: () => setSaving(false) },
            )
          }}
        >
          <Input
            label="Title"
            value={title}
            autoFocus
            hint="Optional."
            onChange={(event) => setTitle(event.target.value)}
          />
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setSaving(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={actions.save.isPending}>
              {actions.save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      ) : (
        <div className={styles.draftActions}>
          <Button onClick={() => setSaving(true)}>Save</Button>
          <Button variant="secondary" onClick={() => onReuse(reuseBlocks(draft))}>
            Refine
          </Button>
          <Button variant="secondary" onClick={() => download(draft, index, downloadName)}>
            Download
          </Button>
          <Button
            variant="danger"
            disabled={actions.discard.isPending}
            onClick={() => actions.discard.mutate({ draftId: draft.draftId })}
          >
            Discard
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Saves a draft to the device.
 *
 * A blob and an object URL rather than an `href` of the base64 itself: a 4K PNG is a
 * multi-megabyte `data:` URL, and browsers refuse those on a download link well before that.
 * The URL is revoked straight after — the click has already been dispatched by then.
 */
function download(draft: ComposerDraft, index: number, name: string) {
  const binary = atob(draft.data)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)

  const url = URL.createObjectURL(new Blob([bytes], { type: draft.contentType }))
  const anchor = document.createElement('a')

  anchor.href = url
  anchor.download = `${name}-${index + 1}.png`
  anchor.click()

  URL.revokeObjectURL(url)
}
